import React, { useState, useEffect } from 'react';
import { 
  ChatAlt2Icon,
  DocumentTextIcon,
  BellIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationIcon,
  PhoneIcon,
  MailIcon,
  LocationMarkerIcon,
  CurrencyDollarIcon,
  TrendingUpIcon,
  StarIcon,
  FilterIcon,
  SearchIcon,
  PaperAirplaneIcon,
  EyeIcon,
  ReplyIcon,
  ArchiveIcon,
  FlagIcon
} from '@heroicons/react/outline';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SupplierNotificationCenter from './SupplierNotificationCenter';

const SupplierCommunicationHub = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);

  // Mock data for demonstration - replace with real data from Firebase
  const mockCommunications = [
    {
      id: 1,
      type: 'quote_response',
      title: 'Response to Office Cleaning RFQ',
      buyer: 'TechCorp Nigeria',
      status: 'pending',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
      priority: 'high',
      budget: 150000,
      category: 'Cleaning',
      unread: true,
      lastMessage: 'Thank you for your quote. Can you provide more details about your cleaning schedule?'
    },
    {
      id: 2,
      type: 'new_rfq',
      title: 'Urgent: 500 Branded Paper Bags',
      buyer: 'PrintMaster Ltd',
      status: 'active',
      timestamp: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
      priority: 'urgent',
      budget: 75000,
      category: 'Printing & Packaging',
      unread: true,
      lastMessage: 'Need delivery within 3 days. Please quote ASAP.'
    },
    {
      id: 3,
      type: 'negotiation',
      title: 'Rice Supply Contract Discussion',
      buyer: 'Restaurant Chain NG',
      status: 'negotiating',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
      priority: 'medium',
      budget: 500000,
      category: 'Food Supply',
      unread: false,
      lastMessage: 'We can offer 10% discount for bulk orders.'
    },
    {
      id: 4,
      type: 'quote_response',
      title: 'IT Equipment Quote Follow-up',
      buyer: 'StartupHub Lagos',
      status: 'won',
      timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
      priority: 'low',
      budget: 200000,
      category: 'Office Equipment',
      unread: false,
      lastMessage: 'Contract signed. Delivery scheduled for next week.'
    }
  ];

  useEffect(() => {
    // Simulate loading data
    setTimeout(() => {
      setCommunications(mockCommunications);
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'negotiating': return 'bg-purple-100 text-purple-800';
      case 'won': return 'bg-green-100 text-green-800';
      case 'lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent': return <ExclamationIcon className="h-4 w-4 text-red-500" />;
      case 'high': return <TrendingUpIcon className="h-4 w-4 text-orange-500" />;
      case 'medium': return <ClockIcon className="h-4 w-4 text-blue-500" />;
      case 'low': return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      default: return <ClockIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'quote_response': return <DocumentTextIcon className="h-5 w-5" />;
      case 'new_rfq': return <BellIcon className="h-5 w-5" />;
      case 'negotiation': return <ChatAlt2Icon className="h-5 w-5" />;
      default: return <DocumentTextIcon className="h-5 w-5" />;
    }
  };

  const filteredCommunications = communications.filter(comm => {
    const matchesSearch = comm.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         comm.buyer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         comm.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || comm.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: communications.length,
    unread: communications.filter(c => c.unread).length,
    pending: communications.filter(c => c.status === 'pending').length,
    active: communications.filter(c => c.status === 'active').length,
    won: communications.filter(c => c.status === 'won').length
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Communication Hub
                </h1>
                <p className="text-gray-600 mt-2">
                  Manage all your buyer communications and opportunities
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => setShowNotifications(true)}
                  className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                >
                  <BellIcon className="h-5 w-5 mr-2 inline" />
                  Notifications
                </button>
                <button 
                  onClick={() => navigate('/market-feed')}
                  className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300"
                >
                  Browse RFQs
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <ChatAlt2Icon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Communications</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <BellIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Unread</p>
                <p className="text-2xl font-bold text-gray-900">{stats.unread}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg">
                <ClockIcon className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUpIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Won</p>
                <p className="text-2xl font-bold text-gray-900">{stats.won}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', name: 'Overview', icon: EyeIcon },
                { id: 'quotes', name: 'My Quotes', icon: DocumentTextIcon },
                { id: 'negotiations', name: 'Negotiations', icon: ChatAlt2Icon },
                { id: 'opportunities', name: 'New Opportunities', icon: BellIcon },
                { id: 'archive', name: 'Archive', icon: ArchiveIcon }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Filters */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search communications..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="negotiating">Negotiating</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Sort by:</span>
                <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent">
                  <option value="recent">Most Recent</option>
                  <option value="priority">Priority</option>
                  <option value="budget">Budget</option>
                  <option value="status">Status</option>
                </select>
              </div>
            </div>
          </div>

          {/* Communications List */}
          <div className="divide-y divide-gray-200">
            {filteredCommunications.length === 0 ? (
              <div className="p-8 text-center">
                <ChatAlt2Icon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No communications found</h3>
                <p className="text-gray-500">Try adjusting your search or filter criteria</p>
              </div>
            ) : (
              filteredCommunications.map((comm) => (
                <div
                  key={comm.id}
                  className={`p-6 hover:bg-gray-50 transition-colors cursor-pointer ${
                    comm.unread ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                  onClick={() => navigate(`/communication/${comm.id}`, { state: { communication: comm } })}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          {getTypeIcon(comm.type)}
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {comm.title}
                          </h3>
                          {comm.unread && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              New
                            </span>
                          )}
                          {getPriorityIcon(comm.priority)}
                        </div>
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                          <span className="flex items-center">
                            <UserGroupIcon className="h-4 w-4 mr-1" />
                            {comm.buyer}
                          </span>
                          <span className="flex items-center">
                            <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                            {formatCurrency(comm.budget)}
                          </span>
                          <span className="flex items-center">
                            <LocationMarkerIcon className="h-4 w-4 mr-1" />
                            {comm.category}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {comm.lastMessage}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end space-y-2">
                      <span className="text-sm text-gray-500">
                        {formatTime(comm.timestamp)}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(comm.status)}`}>
                        {comm.status.charAt(0).toUpperCase() + comm.status.slice(1)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <button className="flex items-center text-sm text-orange-600 hover:text-orange-700">
                        <ReplyIcon className="h-4 w-4 mr-1" />
                        Reply
                      </button>
                      <button className="flex items-center text-sm text-gray-500 hover:text-gray-700">
                        <PhoneIcon className="h-4 w-4 mr-1" />
                        Call
                      </button>
                      <button className="flex items-center text-sm text-gray-500 hover:text-gray-700">
                        <MailIcon className="h-4 w-4 mr-1" />
                        Email
                      </button>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button className="p-1 text-gray-400 hover:text-gray-600">
                        <FlagIcon className="h-4 w-4" />
                      </button>
                      <button className="p-1 text-gray-400 hover:text-gray-600">
                        <ArchiveIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Notification Center Modal */}
      <SupplierNotificationCenter 
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
    </div>
  );
};

export default SupplierCommunicationHub; 